/* eslint-disable react-hooks/rules-of-hooks */
import { AttributeIdentifiers } from "@lyfegen-platform/graphql-schema/models";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import useAsyncSearch from "../hooks/use-async-search";
import { useAvailableDiseases } from "../hooks/useAvailableDiseases";
import { useClearEnrollingConditionSet } from "../hooks/useClearEnrollingConditionSet";
import { ControlState } from "../models/flexible/control-state";
import { FlexControlProps } from "../models/flexible/flex-field-controller";
import { InitialRequestConfig } from "../models/flexible/initial-request";
import { isStartingPoint } from "../models/flexible/typed-flex-entity";
import { Disease, FlexFormStatus, Maybe } from "../models/generated/graphql";
import { GeneralFieldController } from "./general-field-controller";
import { GET_DISEASES_BY_TOKEN, GET_PATIENT_ENTITIES_BY_ENTITY_ID, GET_PATIENT_ENTITIES_BY_ID, GET_SCOPES, whereDrugs } from "./queries";
import { GetDiseasesByToken } from "./types/GetDiseasesByToken";

function getDiseaseLabel(disease: Disease): string {
	return disease && `${disease.Icd10Code} ${disease.Name}`;
}

function mapResult(result?: GetDiseasesByToken): Disease[] {
	return (result?.diseases as Disease[]) || [];
}

function compare(a: { Icd10Code: string }, b: { Icd10Code: string }) {
	return a.Icd10Code === b.Icd10Code;
}

export class DiseaseFieldController extends GeneralFieldController {
	getInitialRequestComponents(): InitialRequestConfig {
		const { entityId = "", patientId = this.parent.context.flexEntity?.data[AttributeIdentifiers.PatientId] } =
			this.parent.context.parameters;

		if (entityId) {
			return {
				queries: [GET_PATIENT_ENTITIES_BY_ENTITY_ID, GET_SCOPES],
				variables: { entityId, where: whereDrugs(this.parent.context.entityType.id, this.parent.context.tenantId) },
			};
		}

		if (patientId) {
			return {
				queries: [GET_PATIENT_ENTITIES_BY_ID, GET_SCOPES],
				variables: { patientId, where: whereDrugs(this.parent.context.entityType.id, this.parent.context.tenantId) },
			};
		}

		return { queries: [], variables: {} };
	}

	override mapInitialValue(value: unknown): unknown {
		if (value || !this.parent.context.cases.length) {
			return super.mapInitialValue(value);
		}

		const caseInstance = this.parent.context.cases[0];
		const disease = caseInstance.flexEntities.find((entity) => entity.data[AttributeIdentifiers.Disease])?.data[
			AttributeIdentifiers.Disease
		];
		return super.mapInitialValue(disease);
	}

	override useControlState(): ControlState {
		if (this.parent.context.flexEntity?.status === FlexFormStatus.Submitted && isStartingPoint(this.parent.context.flexEntity)) {
			return "enabled";
		}

		return this.parent.context.cases?.length && this.parent.context.startingPoint?.data[AttributeIdentifiers.Disease]
			? "disabled"
			: "enabled";
	}

	override useControlProps(): Maybe<FlexControlProps> | undefined {
		const { options, isDiseaseConditionExcluded } = useAvailableDiseases(this);
		const handleClear = useClearEnrollingConditionSet(this.parent.context);
		const {
			searchTerm,
			loading,
			items: searchResultItems,
			handleSetSearchTerm,
		} = useAsyncSearch<GetDiseasesByToken, Disease>(GET_DISEASES_BY_TOKEN, null, mapResult, compare);
		const { t } = useTranslation();
		const actualOptions = searchTerm && isDiseaseConditionExcluded ? searchResultItems : options;

		return {
			controlType: "typeahead",
			getOptionLabel: getDiseaseLabel,
			property: "Icd10Code",
			noOptionsText: t("no_values_found"),
			loading,
			autoSelect: false,
			groupBy: (option: any) => option.group,
			getOptionSelected: compare,
			options: actualOptions,
			onInputChange: isDiseaseConditionExcluded ? handleSetSearchTerm : undefined,
			isChildInFormik: true,
			onClear: isDiseaseConditionExcluded || actualOptions.length > 1 ? undefined : handleClear,
		};
	}

	override getValueTypeProps() {
		return {
			controlType: "typeahead",
			property: "Name",
		};
	}

	protected override createFieldValidationRuleByDataType(): Yup.AnySchema {
		return Yup.object().nullable();
	}
}
